import { useCallback, useEffect, useState } from 'react';
import { isValidPreferredDate, preferredDateLine, todayIso } from './preferredDate';

// The preferred shoot date a visitor chose while enquiring. Kept for this browser
// tab so a choice on one page is included in the WhatsApp or email message.
// A new visit starts blank — an earlier visit must not prefill the date.

const KEY = 'inquiryPreferredDate';
const EVENT = 'inquiry-date-changed';

interface Stored {
  date: string;
  /** True once the visitor has seen the picker, even if they chose "decide later". */
  set: boolean;
}

function read(): Stored {
  try {
    if (typeof window === 'undefined') return { date: '', set: false };
    // Dates saved before this was tab-scoped would show up as a prefilled date.
    try { window.localStorage.removeItem(KEY); } catch { /* ignore */ }
    const raw = window.sessionStorage.getItem(KEY);
    if (!raw) return { date: '', set: false };
    const parsed = JSON.parse(raw) as Partial<Stored>;
    const date = typeof parsed.date === 'string' && isValidPreferredDate(parsed.date) && parsed.date >= todayIso() ? parsed.date : '';
    return { date, set: Boolean(parsed.set) };
  } catch {
    return { date: '', set: false };
  }
}

function write(next: Stored) {
  try {
    try { window.localStorage.removeItem(KEY); } catch { /* ignore */ }
    window.sessionStorage.setItem(KEY, JSON.stringify(next));
    window.dispatchEvent(new Event(EVENT));
  } catch {
    // Private browsing: the choice just will not be remembered.
  }
}

/** "Preferred shoot date: …" once the visitor has had the chance to choose, otherwise nothing. */
export function inquiryDateLine(): string {
  const stored = read();
  return stored.set ? preferredDateLine(stored.date) : '';
}

/** Adds the date line to a plain-text message (WhatsApp, or a decoded email body). */
export function withInquiryDate(message: string): string {
  const line = inquiryDateLine();
  return line ? `${message}\n\n${line}` : message;
}

/**
 * @param showsPicker pass true on a page that displays the picker, so a visitor who leaves it on
 * "decide later" still sends a message that says so.
 */
export function useInquiryDate(showsPicker = false): [string, (value: string) => void] {
  const [stored, setStored] = useState<Stored>(read);

  useEffect(() => {
    const sync = () => setStored(read());
    window.addEventListener(EVENT, sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener(EVENT, sync);
      window.removeEventListener('storage', sync);
    };
  }, []);

  useEffect(() => {
    if (showsPicker && !read().set) write({ date: read().date, set: true });
  }, [showsPicker]);

  const setDate = useCallback((value: string) => {
    write({ date: value && isValidPreferredDate(value) ? value : '', set: true });
  }, []);

  return [stored.date, setDate];
}
